import { Component, EventEmitter, OnInit, Output, Input } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { CalculateFolioSubFormValidator } from './calculate-folio-subform.validator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ListType, TaskType } from 'src/app/shared';
import { ZoningCode } from '../../../../models';
import { ProcessService, ZoningCodeService } from '../../../../services';

import * as _ from 'lodash';
import { DEFAULT_FILE, MAX_FILE_SIZE } from 'src/app/modules/instance/constants';
import { appSettings } from 'src/settings/config';
import { MatCheckboxChange } from '@angular/material/checkbox';

@Component({
    standalone: false,
    selector: 'app-calculate-folio-subform',
    templateUrl: './calculate-folio-subform.component.html',
    styleUrls: ['./calculate-folio-subform.component.scss']
})
export class CalculateFolioSubformComponent implements OnInit {
    @Input()
    showZoningCodeSettings = false;

    @Input()
    forcedModeEnabled = true;

    @Input()
    place = null;

    @Input()
    jobType: TaskType;

    @Output()
    formReady = new EventEmitter<FormGroup>();

    @Output()
    needToRecalculateChange = new EventEmitter<boolean>();

    listForm: FormGroup;
    listType = ListType;

    zoningCodes: Array<ZoningCode>;
    showSyncSolrAUDataCheckbox = appSettings.showSyncSolrAUDataCheckbox;

    constructor(
        private formBuilder: FormBuilder,
        private snackBar: MatSnackBar,
        private readonly zoningCodeService: ZoningCodeService,
        readonly processService: ProcessService
    ) {}

    get isDefaultFile(): boolean {
        return !!_.get(this, 'listForm.value.file.isDefault', false);
    }

    get isPlaceSet(): boolean {
        return !!_.get(this, 'listForm.value.place', false);
    }

    get isZoningCodeSet(): boolean {
        return !!_.get(this, 'listForm.value.zoningCode.GridicsId', false);
    }

    get isPRListRequired(): boolean {
        return this.isDefaultFile || !this.isPlaceSet || !this.isZoningCodeSet;
    }

    get maxThreadsCount() {
        return _.get(this, 'listForm.value.maxThreadsCount', 0);
    }

    get getUseGroupId() {
        return (
            [
                TaskType.CALCULATE_FOLIOS_MULTI_THREAD,
                TaskType.GET_3D_TILES_DATA,
                TaskType.GET_AUTOMATION_DATA_JOB,
                TaskType.CALCULATE_ALL_TYPOLOGIES,
                TaskType.REMOVE_PROPERTY_RECORDS
            ].indexOf(this.jobType) >= 0
        );
    }

    get disableLayers() {
        return (this.listForm.get('type').value === ListType.FOLIOS && this.showLayers) || !this.isDefaultFile;
    }

    get showLayers() {
        return (
            [
                TaskType.CALCULATE_FOLIOS_MULTI_THREAD,
                TaskType.GET_3D_TILES_DATA,
                TaskType.GET_AUTOMATION_DATA_JOB,
                TaskType.CALCULATE_ALL_TYPOLOGIES,
                TaskType.REMOVE_PROPERTY_RECORDS,
                TaskType.RECALCULATE_LISTED_BY_STATUS_JOB
            ].indexOf(this.jobType) >= 0
        );
    }

    static getQueryParams(formGroup: any, showZoningCodeSettings = false) {
        const params: any = {
            title: formGroup['type'],
            value: formGroup['list'] && formGroup['list'].split('\n').filter(e => e),
            file: formGroup['file'],
            needToRecalculate: formGroup['needToRecalculate'],
            maxThreadsCount: formGroup['maxThreadsCount'],
            dataSourceCategories: formGroup['layers']
        };

        if (showZoningCodeSettings) {
            const zcGridicsId = _.get(formGroup, 'zoningCode.GridicsId');
            params.place = formGroup['place'] ? formGroup['place'].trim() : null;
            if (!params.place && zcGridicsId) {
                params.place = zcGridicsId;
            }
            const zoningCodeId = _.get(formGroup, 'zoningCode.Id');
            if (!params.zoningCodeId && zoningCodeId) {
                params.zoningCodeId = zoningCodeId;
            }
        }

        return params;
    }

    ngOnInit() {
        this.zoningCodes = [{ GridicsId: null, Name: 'None' } as any, ...this.zoningCodeService.lastZoningCodesValue];
        const selectedZCIndex = this.place ? this.zoningCodes.findIndex(zc => zc.GridicsId === this.place) : 0;
        this.listForm = this.formBuilder.group(
            {
                type: new FormControl(ListType.FOLIOS),
                list: new FormControl(''),
                file: {},
                needToRecalculate: false,
                zoningCode: new FormControl({
                    value: selectedZCIndex >= 0 ? this.zoningCodes[selectedZCIndex] : this.zoningCodes[0],
                    disabled: !!this.place && selectedZCIndex < 0
                }),
                place: new FormControl({ value: this.place || '', disabled: selectedZCIndex > 0 }),
                maxThreadsCount: new FormControl(1),
                layers: this.formBuilder.group({
                    city: new FormControl(true),
                    county: new FormControl(true),
                    state: new FormControl(true)
                })
            },
            { validators: [CalculateFolioSubFormValidator.validateFolioList] }
        );

        this.setupEmptyFile();
        this.formReady.emit(this.listForm);
    }

    uploadFile(event) {
        const file = event.target.files[0];
        if (file.size > MAX_FILE_SIZE) {
            this.snackBar.open(`Cannot attach file larger than 6Mb`, 'OK', {
                verticalPosition: 'top',
                horizontalPosition: 'center',
                duration: 3000
            });
            return;
        }
        this.listForm.controls.file.setValue(file);
        this.listForm.controls.list.disable();
        this.listForm.controls.type.disable();
    }

    setupEmptyFile() {
        this.listForm.controls.file.setValue(DEFAULT_FILE);
        this.listForm.controls.list.enable();
        this.listForm.controls.type.enable();
    }

    setupZCPlaceId(event) {
        const gridicsId = _.get(event, 'value.GridicsId', '');
        this.listForm.controls.place.setValue(gridicsId);

        if (gridicsId) {
            this.listForm.controls.place.disable();
        } else {
            this.listForm.controls.place.enable();
        }
    }

    safeValue(value: number | null) {
        if (!value) {
            return 0;
        }

        return value;
    }

    onNeedToRecalculateChange(event: MatCheckboxChange) {
        this.needToRecalculateChange.emit(event.checked);
    }
}
